import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { appTheme } from '@dotstorming/main-theme';
import { StrictMode, type ReactNode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';

import App from './app/app';
import './styles.scss';

export type WallsRouterType = 'browser' | 'memory';

export interface WallsMountOptions {
  basename?: string;
  initialPath?: string;
  router?: WallsRouterType;
  strictMode?: boolean;
}

export interface WallsMountHandle {
  unmount: () => void;
}

const mountedRoots = new WeakMap<HTMLElement, ReactDOM.Root>();

const withRouter = (options: WallsMountOptions, children: ReactNode) => {
  const router = options.router ?? 'browser';
  const basename = options.basename;

  if (router === 'memory') {
    return (
      <MemoryRouter basename={basename} initialEntries={[options.initialPath ?? '/']}>
        {children}
      </MemoryRouter>
    );
  }

  return <BrowserRouter basename={basename}>{children}</BrowserRouter>;
};

const resolveContainer = (target: string | HTMLElement) => {
  if (typeof target === 'string') {
    const node = document.querySelector<HTMLElement>(target);
    if (!node) {
      throw new Error(`Could not find walls mount target: ${target}`);
    }

    return node;
  }

  return target;
};

export const mountWalls = (
  target: string | HTMLElement,
  options: WallsMountOptions = {}
): WallsMountHandle => {
  const container = resolveContainer(target);
  const existingRoot = mountedRoots.get(container);

  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = ReactDOM.createRoot(container);
  mountedRoots.set(container, root);

  const app = withRouter(
    options,
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  );
  const shouldUseStrictMode = options.strictMode ?? false;

  root.render(shouldUseStrictMode ? <StrictMode>{app}</StrictMode> : app);

  return {
    unmount: () => {
      const currentRoot = mountedRoots.get(container);
      if (!currentRoot) {
        return;
      }

      currentRoot.unmount();
      mountedRoots.delete(container);
    },
  };
};

declare global {
  interface Window {
    DotstormingWalls?: {
      mountWalls: typeof mountWalls;
    };
  }
}

window.DotstormingWalls = {
  mountWalls,
};
